/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useState } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { WidthObserver } from '@atlaskit/width-detector';

import { CommonLayout, Height } from '../../types';

const CONTENT_WIDTH = 800;

const styles = cssMap({
	default: {
		width: `${CONTENT_WIDTH}px`,
		height: '122px',
	},
	compact: {
		height: '96px',
	},
	containerStyles: {
		width: '100%',
		overflow: 'hidden',
		borderRadius: token('radius.large'),
		position: 'relative',
	},
});

export type PreviewSizeVariant = 'default' | 'compact';

export type PreviewSizeProps<TLayout> = {
	layout?: TLayout;
	/**
	 * A React component used to render a preview.
	 *
	 * @param {Height} height
	 *   The height at which to render the preview.
	 * @param {TLayout} layout
	 *   The layout variant to apply when rendering the preview.
	 * @returns {JSX.Element}
	 *   The rendered preview element.
	 */
	PreviewComponent: React.ComponentType<{
		height: Height;
		layout: TLayout;
	}>;
	/**
	 * Several sizing options impact the appearance of the preview.
	 * This prop considers both compact and default sizing in tandem with
	 * LayoutOptions component.
	 */
	previewSizeVariant?: PreviewSizeVariant;
};

export const PreviewSize = <TLayout,>({
	layout = CommonLayout.SPLIT_IMAGE_RIGHT as TLayout,
	previewSizeVariant = 'default',
	PreviewComponent,
}: PreviewSizeProps<TLayout>): JSX.Element => {
	const [containerWidth, setContainerWidth] = useState(0);
	const calculatedScale = containerWidth ? containerWidth / CONTENT_WIDTH : 0;

	return (
		<div css={styles.containerStyles}>
			<WidthObserver setWidth={setContainerWidth} />
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop */}
			<div style={{ transform: `scale(${calculatedScale})`, transformOrigin: 'top left' }}>
				<div css={[styles.default, styles[previewSizeVariant]]}>
					<PreviewComponent height={Height.MEDIUM} layout={layout} />
				</div>
			</div>
		</div>
	);
};
